<script setup async>
import {competitionFacts} from '~/utils/privateAccess';
const {outputDateFromDb} = useCrudConverters();
import CompetitionQuestionsPrompt from "@/components/competitions/CompetitionQuestionsPrompt.vue";
import FormatExplainerDialog from '~/components/formats/FormatExplainerDialog.vue';
// MEET HUB (mockups meet.html / meet-join.html / meet-empty.html).
// The Meet IS the event: members join once, then RSVP per date below.
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {createError} from 'nuxt/app';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useMeetFormat, AttendanceStatus, OccurrenceStatus, myUpcomingBookings, occurrencesMissingDetail} from '~/composables/useMeetFormat';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useMailer} from '~/composables/useMailer';
import {useMessaging} from '~/composables/useMessaging';
import {endpoints} from '~/network/endpoints.js';
import FrontendHeader from '~/components/layout/FrontendHeader.vue';
import ClubChip from '~/components/clubs/ClubChip.vue';
import ShortLinkShareWrapper from '~/components/share/ShortLinkShareWrapper.vue';
import InformationDialog from '~/components/InformationDialog.vue';
import ArchivedNotice from '~/components/ArchivedNotice.vue';
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import JoinRequestsCard from '~/components/join/JoinRequestsCard.vue';
import JoinRequestPending from '~/components/join/JoinRequestPending.vue';
import MeetOccurrenceCard from '~/components/meets/MeetOccurrenceCard.vue';
import MeetAttendeeList from '~/components/meets/MeetAttendeeList.vue';
import MeetRsvpDialog from '~/components/meets/MeetRsvpDialog.vue';
import MeetLeaveDialog from '~/components/meets/MeetLeaveDialog.vue';
import MeetMembersManageDialog from '~/components/meets/MeetMembersManageDialog.vue';

definePageMeta({
  name: 'MeetDetail',
  path: '/meets/:meetId',
});

const {item: meet, getItem: getMeet, hasItemErrors} = useCrudApi('api/v2/meets');

const route = useRoute();
const router = useRouter();
const meetId = route.params.meetId;

await getMeet(meetId);

if (hasItemErrors.value) {
  throw createError({
    statusCode: 404,
    message: 'Meet not found. Has it been deleted?',
  });
}

const {setMeta} = useHeadService();
setMeta(
    meet.value?.clubName + ' / ' + meet.value?.name + ' on Tap Leagues',
    meet.value?.clubName + ' / ' + meet.value?.name + ' meet on Tap Leagues. See upcoming dates, who\'s going and mark yourself in.',
);

const {isAuthenticated, user, isAdminOfClub, isMemberOfClub} = useAuthService();
const {addListener, removeLocalListenersWithEvent} = useListenerService();
const {httpRequest} = useHttpRequest();
const {filtersToQuery} = useMailer();
const {
  recurrenceText, scheduleChipText, isPaid, feeText, dateFeeText, chargeTimingText,
  occurrenceDay, occurrencePast, occurrenceCancelled, rsvpClosed, rsvpNotYetOpen, rsvpOpensText, isFull, capacityText,
} = useMeetFormat();

const meetArchived = computed(() => !!meet.value?.archivedAt);
const meetPrivate = computed(() => !!meet.value?.privateToViewer);
usePrivatePage(meetPrivate);
const isAdmin = computed(() => meet.value && isAdminOfClub(meet.value.clubId));
// The roster (MeetPlayers) — EVERY meet has one, and only roster players can
// RSVP (admins included; no bypass). Open vs private only changes how the
// meet's join link behaves.
const isMember = computed(() =>
    !!user.value?.playerId && (meet.value?.players ?? []).some(m => m.playerId === user.value.playerId));
const isPrivate = computed(() => !!meet.value?.isPrivate);
const isClubMember = computed(() => !!meet.value && isMemberOfClub(meet.value.clubId));
const canParticipate = computed(() => isMember.value);
const canSeeRoster = computed(() => isMember.value || isAdmin.value);
// The managed visible join link — the ONLY way in (ladder-page pattern).
const joinLink = computed(() => {
  const link = meet.value?.visibleInviteLink;
  return link && link.isActive !== false ? link : null;
});

// ── occurrences: upcoming (asc, scheduled only) · past · cancelled ──
// Cancelled dates get their own quiet tab — they must never sit at the top of
// the Upcoming list shouting louder than the dates that are actually on.
const upcoming = computed(() => (meet.value?.occurrences ?? [])
    .filter(o => !occurrencePast(o) && !occurrenceCancelled(o)));
const pastDates = computed(() => [...(meet.value?.occurrences ?? [])]
    .filter(o => occurrencePast(o) && !occurrenceCancelled(o))
    .reverse()
    .slice(0, 5));
const cancelledDates = computed(() => [...(meet.value?.occurrences ?? [])]
    .filter(o => occurrenceCancelled(o))
    .reverse()
    .slice(0, 5));

// Dates tab (tl-seg, same pattern as the ladder page) — synced to the route
// query so back/forward keeps the view. replace, never push.
const validDateTabs = ['upcoming', 'past', 'cancelled'];
const dateTab = computed({
  get: () => validDateTabs.includes(route.query.dates) ? route.query.dates : 'upcoming',
  set: (newTab) => {
    if (route.query.dates === newTab) return;
    router.replace({query: {...route.query, dates: newTab === 'upcoming' ? undefined : newTab}});
  },
});

// ── my per-date RSVP state ──
// MeetOccurrenceListResponse carries counts only — myAttendance rides the
// occurrence DETAIL, so the hub hydrates the next few dates client-side.
const occurrenceDetails = ref({}); // occurrenceId -> MeetOccurrenceDetailResponse
const detailsLoaded = ref(false);
const loadMyAttendances = async () => {
  if (!canParticipate.value) return;
  const targets = upcoming.value.slice(0, 8);
  const results = await Promise.allSettled(
      targets.map(o => httpRequest(endpoints.meetOccurrences.getItem(o.id), null)));
  const map = {};
  results.forEach((r, i) => {
    if (r.status === 'fulfilled' && r.value) map[targets[i].id] = r.value;
  });
  occurrenceDetails.value = map;
  detailsLoaded.value = true;
};

const myAttendanceFor = (occurrence) => detailsLoaded.value
    ? (occurrenceDetails.value[occurrence.id]?.myAttendance ?? null)
    : undefined;

// The NEAREST unanswered, open, scheduled date carries the inline RSVP buttons
// (one primary action on the page — plan §3.2).
const nearestUnanswered = computed(() => {
  if (!detailsLoaded.value) return null;
  return upcoming.value.find(o =>
      o.status === OccurrenceStatus.Scheduled
      && occurrenceDetails.value[o.id]
      && occurrenceDetails.value[o.id].myAttendance == null
      && !rsvpClosed(meet.value, o)
      && !rsvpNotYetOpen(o)
      && !isFull(meet.value, o)) ?? null;
});

// Dates leaving would cancel (for the leave dialog's outcome rows)
// Fed the DETAIL occurrences: they carry myCancellation, so each date is priced
// from its booking's agreed terms (what the server's Leave charges).
const myUpcoming = computed(() => myUpcomingBookings(upcoming.value, occurrenceDetails.value));

// ── inline RSVP (nearest date) ──
const rsvpDialogOpen = ref(false);
const inlinePending = ref(false);
const refresh = async () => {
  await getMeet(meetId);
  await loadMyAttendances();
};

const inlineRsvp = async (attending) => {
  const target = nearestUnanswered.value;
  if (!target || inlinePending.value) return;
  // Paid meets confirm through the dialog; free meets are ONE TAP.
  if (attending && isPaid(meet.value)) {
    rsvpDialogOpen.value = true;
    return;
  }
  inlinePending.value = true;
  try {
    await httpRequest(endpoints.meetOccurrences.rsvp(target.id), {attending});
    await refresh();
  } finally {
    inlinePending.value = false;
  }
};

// ── entity chat (ladder-page pattern): members and club admins can start or
// join the meet's group chat; the API syncs participants with the roster. ──
const {entityChatLoading, createOrOpenEntityChat} = useMessaging();
const canAccessChat = computed(() => isAuthenticated.value && (isMember.value || isAdmin.value));
const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {meetId: meet.value.id, conversationId: meet.value.conversationId},
      () => getMeet(meetId),
  );
};

// ── admin self-join: admins approve requests, so asking them to request their
// own membership is silly — they go straight onto the roster. ──
const adminJoinPending = ref(false);
const adminJoin = async () => {
  if (adminJoinPending.value || !user.value?.playerId) return;
  adminJoinPending.value = true;
  try {
    const res = await httpRequest(endpoints.meets.addPlayer(meet.value.id), {playerId: user.value.playerId});
    if (res?.id) meet.value = res;
    await refresh();
  } finally {
    adminJoinPending.value = false;
  }
};

// ── pending join request (private meets: the visible link requires approval) ──
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('meet', computed(() => meet.value?.id));

// ── leave ──
const leaveDialogOpen = ref(false);
const leaveOpening = ref(false);
// The hub only hydrates the next few dates, so load every other upcoming
// date's detail before the dialog shows amounts: a booking further out must be
// listed, and priced from its own agreed terms.
const openLeaveDialog = async () => {
  if (leaveOpening.value) return;
  leaveOpening.value = true;
  try {
    const ids = occurrencesMissingDetail(upcoming.value, occurrenceDetails.value);
    const results = await Promise.allSettled(
        ids.map(id => httpRequest(endpoints.meetOccurrences.getItem(id), null)));
    const fetched = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) fetched[ids[i]] = r.value;
    });
    if (ids.length) occurrenceDetails.value = {...occurrenceDetails.value, ...fetched};
  } finally {
    leaveOpening.value = false;
    leaveDialogOpen.value = true;
  }
};

// ── manage members in place (admins) — no admin-area detour ──
const membersDialogOpen = ref(false);
const onMeetUpdated = (fresh) => {
  if (fresh?.id) meet.value = fresh;
};

const titleActions = computed(() => {
  const actions = [];
  if (isAdmin.value) {
    actions.push({
      // The admin meet page runs EVERYTHING (dates, register, matches,
      // members, settings) — never call it "Manage dates".
      title: 'Manage meet',
      icon: 'mdi-cog',
      click: () => router.push({name: 'ClubMeetManage', params: {clubId: meet.value.clubId, meetId: meet.value.id}}),
      color: 'info',
    });
  }
  return actions;
});

onMounted(() => {
  loadMyAttendances();
  addListener('requestsUpdated', () => getMeet(meetId));
});

onBeforeUnmount(() => {
  removeLocalListenersWithEvent('requestsUpdated');
});
</script>

<template>
  <base-page>
    <!-- Deleted (archived) meet - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): identity only, then the notice where the content would be -->
    <v-container v-if="meetPrivate" class="large">
      <frontend-header :meet="meet"
          :club="meet.club ?? (meet.clubId ? {id: meet.clubId, slug: meet.clubId, name: meet.clubName} : null)"
          :title="meet.name"
          title-icon="mdi-handshake"
          title-icon-color="success"
      >
        <template #subTitle>
          <club-chip v-if="meet.club" :club="meet.club"></club-chip>
          <span v-else-if="meet.clubName" class="tl-chip">{{ meet.clubName }}</span>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <private-notice reason="competition" entity="meet" :name="meet.name" :club-name="meet.clubName" :join-slug="meet.visibleInviteLink?.slug"
                      :facts="competitionFacts('meet', meet, d => outputDateFromDb(d, 'd MMM yyyy'))"/>
    </v-container>
    <v-container v-else-if="meetArchived" class="large">
      <archived-notice entity="meet" :name="meet.name"/>
    </v-container>
    <v-container v-else-if="meet" class="large">

      <!-- Page head: h1 + chips (club / sport / schedule / fee) -->
      <frontend-header
          :club="meet.club" :meet="meet" :actions="titleActions"
          title-icon="mdi-handshake" title-icon-color="success"
      >
        <template #title>
          <h1 class="mb-0">{{ meet.name }}</h1>
          <div class="head-chips">
            <club-chip :club="meet.club"/>
            <span class="tl-chip tl-chip--ok">
              <v-icon size="13" icon="mdi-handshake" class="mr-1"/>Meet · {{ recurrenceText(meet) }}
            </span>
            <span v-if="meet.sport" class="tl-chip">{{ sportDisplayName(meet.sport) }}</span>
          </div>
        </template>
      </frontend-header>

      <!-- Action strip: actions + the facts chips (when + price live here ONCE) -->
      <div class="tl-action-strip my-4">
        <short-link-share-wrapper
                    :join-reference-type="5" :join-reference-id="meet.id" :can-share-join="isAdmin">
          <template #default="{click, loading: shareLoading}">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <router-link class="as" :to="{name: 'MeetStats', params: {meetId: meet.id}}">
          <v-icon size="17" icon="mdi-chart-line"/>Stats
        </router-link>

        <router-link class="as" :to="{name: 'FixtureSearch', query: {clubIds: [meet.clubId], meetIds: [meet.id]}}">
          <v-icon size="17" icon="mdi-scoreboard"/>View Matches
        </router-link>

        <!-- Information (admin-authored rich text) — only when content exists -->
        <format-explainer-dialog format="meet" :admin="isAdmin" :payments="meet.feeMinor != null"/>
        <information-dialog :html="meet.informationHtml" :title="`${meet.name} information`"/>

        <!-- Chat (ladder-page pattern) -->
        <button v-if="canAccessChat" class="as" :disabled="entityChatLoading" @click="handleChatClick">
          <v-icon size="17" icon="mdi-forum"/>{{ meet.conversationId ? 'Join Chat' : 'Start Chat' }}
        </button>

        <!-- Admin: email every roster member (ladder/league pattern) -->
        <router-link
            v-if="isAdmin"
            class="as"
            :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [meet.clubId], meetIds: [meet.id] }) } }"
        >
          <v-icon size="17" icon="mdi-email"/>Email members
        </router-link>

        <!-- Leave (roster members) → E5 consequences dialog -->
        <button v-if="isMember" class="as" :disabled="leaveOpening" @click="openLeaveDialog">
          <v-icon size="17" icon="mdi-exit-to-app"/>Leave
        </button>

        <!-- Pending request pill (non-members who applied) -->
        <join-request-pending
            v-if="!isMember && !isAdmin && hasPendingRequest"
            compact
            :entity-name="meet.name"
            :cancelling="cancelPending"
            @cancel="cancelRequest"
        />

        <div class="vsep"></div>
        <span class="tl-chip">
          <v-icon size="13" icon="mdi-calendar-sync" class="mr-1"/>{{ scheduleChipText(meet) }}
        </span>
        <span class="tl-chip">
          <v-icon size="13" icon="mdi-cash" class="mr-1"/>
          {{ isPaid(meet) ? feeText(meet) : 'Free' }}
        </span>
      </div>

      <!-- Admin invite CTA — the join link is how players reach the roster -->
      <join-settings-card
          v-if="isAdmin"
          class="my-4"
          :reference-type="5"
          :reference-id="meet.id"
          :club-id="meet.clubId"
          :entity-name="meet.name"
          @changed="getMeet(meetId)"
      />

      <!-- Pending join requests (admins; count rides on the meet response) -->
      <join-requests-card
          v-if="isAdmin"
          :count="meet.accessApplicationCount ?? 0"
          :filters="{meetIds: [meet.id]}"
      />

      <v-row align="start">
        <!-- ========== MAIN COLUMN ========== -->
        <v-col cols="12" md="8">

          <!-- Admin not on the roster: straight in, no request/approval dance -->
          <div v-if="!isMember && isAdmin" class="tl-card join-cta">
            <div class="join-cta__ic"><v-icon color="primary" icon="mdi-shield-account-outline"/></div>
            <div class="flex-fill">
              <div class="join-cta__title">Join this meet to RSVP</div>
              <div class="join-cta__sub">
                You're a club admin, so you can join straight away{{ isPrivate ? ', with no request or approval needed' : '' }}.
                Once you're on the roster you can mark yourself in for any date.
              </div>
            </div>
            <v-btn
                color="primary"
                rounded="pill"
                prepend-icon="mdi-account-plus"
                :loading="adminJoinPending"
                @click="adminJoin"
            >
              Join
            </v-btn>
          </div>

          <!-- Not on the roster: join through the meet's visible link — open
               meets auto-join club members, private ones raise an approval
               request (same /invite flow as ladders). -->
          <div v-else-if="!isMember && !hasPendingRequest && joinLink" class="tl-card join-cta">
            <div class="join-cta__ic" :class="{'join-cta__ic--success': !isPrivate}">
              <v-icon :color="isPrivate ? 'primary' : 'success'"
                      :icon="isPrivate ? 'mdi-lock-outline' : 'mdi-handshake'"/>
            </div>
            <div class="flex-fill">
              <div class="join-cta__title">
                {{ isPrivate ? 'Request to join this meet' : 'Join this meet to RSVP' }}
              </div>
              <div class="join-cta__sub">
                {{ isPrivate
                  ? 'A club admin approves each request. Once you are in, you can RSVP to any date.'
                  : `Open to all ${meet.clubName ?? 'club'} members. Join once and mark yourself in for any date.` }}
              </div>
            </div>
            <v-btn
                color="primary"
                rounded="pill"
                :prepend-icon="isPrivate ? 'mdi-account-clock' : 'mdi-account-plus'"
                :to="`/invite/${joinLink.slug}`"
            >
              {{ isPrivate ? 'Request to join' : 'Join' }}
            </v-btn>
          </div>

          <!-- No live join link (older meet awaiting a settings save) -->
          <div v-else-if="!isMember && !hasPendingRequest" class="tl-card join-cta">
            <div class="join-cta__ic"><v-icon color="primary" icon="mdi-lock-outline"/></div>
            <div class="flex-fill">
              <div class="join-cta__title">Joining is invite-only right now</div>
              <div class="join-cta__sub">
                A club admin can add you to this meet, or send you an invite link.
              </div>
            </div>
          </div>

          <!-- About (informationHtml, inline for non-participants) -->
          <template v-if="!canParticipate && meet.informationHtml">
            <div class="tl-section-head"><h2>About</h2></div>
            <div class="tl-card about-card" v-html="meet.informationHtml"/>
          </template>

          <!-- ════════ DATES — Upcoming · Past · Cancelled (tl-seg tabs) ════════ -->
          <div class="tl-section-head"><h2>Dates</h2></div>
          <div v-if="pastDates.length > 0 || cancelledDates.length > 0" class="tl-seg mb-4">
            <button
                class="tl-seg__opt"
                :class="{'tl-seg__opt--active': dateTab === 'upcoming'}"
                @click="dateTab = 'upcoming'"
            >Upcoming</button>
            <button
                v-if="pastDates.length > 0"
                class="tl-seg__opt"
                :class="{'tl-seg__opt--active': dateTab === 'past'}"
                @click="dateTab = 'past'"
            >Past</button>
            <button
                v-if="cancelledDates.length > 0"
                class="tl-seg__opt"
                :class="{'tl-seg__opt--active': dateTab === 'cancelled'}"
                @click="dateTab = 'cancelled'"
            >Cancelled</button>
          </div>

          <template v-if="dateTab === 'upcoming'">
          <!-- Non-members don't RECEIVE dates at all (the API strips them),
               so their empty state says "join to see", not "none scheduled". -->
          <div v-if="upcoming.length === 0 && !isMember && !isAdmin" class="tl-card empty-card">
            <v-icon size="34" icon="mdi-lock-outline" class="mb-2" color="textSubtle"/>
            <div class="empty-card__t">Dates are for members</div>
            <div class="empty-card__s">
              Join the meet to see the upcoming dates and who's going.
            </div>
          </div>
          <!-- "No dates yet" empty state (meet-empty.html) -->
          <div v-else-if="upcoming.length === 0" class="tl-card empty-card">
            <v-icon size="34" icon="mdi-calendar-blank-outline" class="mb-2" color="textSubtle"/>
            <div class="empty-card__t">No dates yet</div>
            <div class="empty-card__s">
              <template v-if="isAdmin">
                Add your first date from the meets admin page. Members are notified when one lands.
              </template>
              <template v-else>
                The club hasn't scheduled a date yet. You'll be notified when one lands.
              </template>
            </div>
            <v-btn
                v-if="isAdmin"
                color="primary"
                rounded="pill"
                class="mt-3"
                prepend-icon="mdi-calendar-plus"
                :to="{name: 'ClubMeets', params: {clubId: meet.clubId}}"
            >
              Add your first date
            </v-btn>
          </div>

          <meet-occurrence-card
              v-for="occurrence in upcoming"
              :key="occurrence.id"
              :meet="meet"
              :occurrence="occurrence"
              :my-attendance="myAttendanceFor(occurrence)"
              :obfuscated="!canParticipate"
          >
            <!-- inline one-tap RSVP on the nearest unanswered date only (the
                 not-open banner is baked into the card itself) -->
            <template v-if="nearestUnanswered && nearestUnanswered.id === occurrence.id" #rsvp>
              <v-btn
                  color="primary"
                  size="small"
                  rounded="pill"
                  prepend-icon="mdi-check"
                  :loading="inlinePending"
                  @click="inlineRsvp(true)"
              >
                Count me in
              </v-btn>
              <v-btn
                  variant="outlined"
                  size="small"
                  rounded="pill"
                  prepend-icon="mdi-close"
                  :disabled="inlinePending"
                  @click="inlineRsvp(false)"
              >
                Can't make it
              </v-btn>
              <span v-if="isPaid(meet)" class="rsvp-note">
                {{ dateFeeText(meet, occurrenceDetails[occurrence.id]) }} · {{ chargeTimingText(meet) }} · nothing taken now.
              </span>
            </template>
          </meet-occurrence-card>

          <div v-if="!canParticipate && !hasPendingRequest && upcoming.length > 0" class="join-note">
            <template v-if="isAdmin">Join this meet above (as an admin you're in straight away) and you can mark yourself in for any date.</template>
            <template v-else-if="isPrivate">Request to join this meet above. Once approved you can mark yourself in for any date.</template>
            <template v-else>Join this meet above and you can mark yourself in for any date.</template>
          </div>
          </template>

          <!-- ════════ PAST (summaries → occurrence pages) ════════ -->
          <template v-else-if="dateTab === 'past'">
            <div class="tl-card">
              <router-link
                  v-for="occurrence in pastDates"
                  :key="occurrence.id"
                  :to="{name: 'MeetOccurrenceDetail', params: {meetId: meet.id, occurrenceId: occurrence.id}}"
                  class="past-row"
              >
                <div class="past-row__tile"><v-icon size="18" icon="mdi-history"/></div>
                <div class="flex-fill min-w-0">
                  <div class="past-row__t">{{ occurrenceDay(occurrence) }}</div>
                  <div class="past-row__s">{{ occurrence.attendingCount }} attended</div>
                </div>
                <v-icon size="18" icon="mdi-chevron-right" class="past-row__chev"/>
              </router-link>
              <div v-if="pastDates.length === 0" class="past-row past-row--empty">No past dates yet.</div>
            </div>
          </template>

          <!-- ════════ CANCELLED — quiet rows, same weight as past ════════ -->
          <template v-else>
            <div class="tl-card">
              <router-link
                  v-for="occurrence in cancelledDates"
                  :key="occurrence.id"
                  :to="{name: 'MeetOccurrenceDetail', params: {meetId: meet.id, occurrenceId: occurrence.id}}"
                  class="past-row"
              >
                <div class="past-row__tile"><v-icon size="18" icon="mdi-cancel"/></div>
                <div class="flex-fill min-w-0">
                  <div class="past-row__t">{{ occurrenceDay(occurrence) }}</div>
                  <div class="past-row__s">Cancelled by the club, so no charges apply</div>
                </div>
                <v-icon size="18" icon="mdi-chevron-right" class="past-row__chev"/>
              </router-link>
              <div v-if="cancelledDates.length === 0" class="past-row past-row--empty">No cancelled dates.</div>
            </div>
          </template>
        </v-col>

        <!-- ========== SIDE COLUMN ========== -->
        <v-col cols="12" md="4">
          <div class="side-sticky">
            <!-- The roster — every meet has one; only roster players can RSVP. -->
            <div class="tl-section-head mt-0">
              <h2>Members · {{ meet.playerCount ?? meet.players?.length ?? 0 }}</h2>
              <!-- Same section-link idiom as the event page's Manage links -->
              <a v-if="isAdmin" class="tl-section-link" @click.prevent="membersDialogOpen = true">Manage</a>
            </div>
            <meet-attendee-list
                v-if="canSeeRoster"
                :rows="meet.players"
                :my-player-id="user?.playerId"
            >
              <template #empty>No members yet</template>
            </meet-attendee-list>
            <div v-else class="tl-card obfuscated-card">
              <v-icon size="18" icon="mdi-eye-off-outline" class="mr-2"/>
              The member list is visible once you've joined.
            </div>

            <div class="tl-section-head"><h2>Actions</h2></div>
            <div class="tl-card">
              <router-link :to="{name: 'ClubDetail', params: {clubId: meet.clubSlug ?? meet.clubId}}" class="past-row">
                <div class="past-row__tile"><v-icon size="18" icon="mdi-account-group"/></div>
                <div class="flex-fill min-w-0"><div class="past-row__t">{{ meet.clubName }}</div></div>
                <v-icon size="18" icon="mdi-chevron-right" class="past-row__chev"/>
              </router-link>
            </div>
          </div>
        </v-col>
      </v-row>

      <meet-members-manage-dialog
          v-if="isAdmin"
          v-model="membersDialogOpen"
          :meet="meet"
          @updated="onMeetUpdated"
      />

      <!-- Paid RSVP flow for the nearest date's inline "Count me in" -->
      <meet-rsvp-dialog
          v-if="nearestUnanswered && occurrenceDetails[nearestUnanswered.id]"
          v-model="rsvpDialogOpen"
          :meet="meet"
          :occurrence="occurrenceDetails[nearestUnanswered.id]"
          @updated="refresh"
      />

      <!-- Leave (E5): future RSVPs cancelled with tier outcomes -->
      <meet-leave-dialog
          v-model="leaveDialogOpen"
          :meet="meet"
          :my-upcoming="myUpcoming"
          @left="refresh"
      />
    </v-container>
    <competition-questions-prompt competition-type="meet" :competition-id="meet?.id"/>
  </base-page>
</template>

<style scoped lang="scss">
.head-chips {
  display: flex;
  gap: 6px;
  margin-top: 9px;
  flex-wrap: wrap;
  align-items: center;
}

.side-sticky {
  position: sticky;
  top: 84px;
}

/* non-member join CTA (meet-join.html) */
.join-cta {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  margin-bottom: 4px;

  &__ic {
    width: 42px;
    height: 42px;
    border-radius: 13px;
    background: rgb(var(--v-theme-brandTint));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    /* the meet identity icon is ALWAYS success-on-faded-success */
    &--success {
      background: rgb(var(--v-theme-successTint));
    }
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  &__sub {
    font-size: 12px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 3px;
    line-height: 1.6;
  }
}

.about-card {
  padding: 16px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.7;
}

.join-note,
.rsvp-note {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  font-weight: 500;
}

.join-note {
  margin: 8px 2px 0;
}

/* "no dates yet" (meet-empty.html · states-empty.html canon) */
.empty-card {
  padding: 28px 16px;
  text-align: center;

  &__t {
    font-size: 14px;
    font-weight: 700;
  }

  &__s {
    font-size: 12px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 4px;
    line-height: 1.6;
  }
}

/* past-date / action rows (mockup .comp-row) */
.past-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 16px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));

  & + .past-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &--empty {
    color: rgb(var(--v-theme-textSubtle));
    font-size: 13px;

    &:hover {
      background: none;
    }
  }

  &__tile {
    width: 36px;
    height: 36px;
    border-radius: 11px;
    background: rgb(var(--v-theme-bg));
    color: rgb(var(--v-theme-charcoal));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__t {
    font-size: 13px;
    font-weight: 700;
  }

  &__s {
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 1px;
  }

  &__chev {
    color: rgb(var(--v-theme-textSubtle));
    flex-shrink: 0;
  }
}

.obfuscated-card {
  padding: 16px;
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  line-height: 1.6;
  display: flex;
  align-items: center;
}
</style>
